import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { getSimulations } from '@/api/dashboardService';
import { parseApiError } from '@/utils/errorHandler';
import { AppRoutes } from '@/routes/routeMap';
import type { simulation } from '../dashboard/types.api';
import type { parsedApiError } from '@/types/api/errors.api';

export const useStudentHome = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [simulations, setSimulations] = useState<simulation[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<parsedApiError | null>(null);

    useEffect(() => {
        const fetchUserSimulations = async () => {
            if (!user) {
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            try {
                const userSimulations = await getSimulations(user.id as unknown as string);
                setSimulations(userSimulations);
            } catch (err) {
                setError(parseApiError(err));
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserSimulations();
    }, [user]);

    const hasVirtualSimulations = useMemo(() => {
        return simulations.some(sim => sim.type === 'virtual');
    }, [simulations]);

    const handleNavigateToDashboard = () => {
        navigate(AppRoutes.STUDENT_DASHBOARD);
    };

    const handleNavigateToVirtual = () => {
        navigate('/student/virtual-simulations');
    };

    return {
        isLoading,
        error,
        user,
        hasVirtualSimulations,
        handleNavigateToDashboard,
        handleNavigateToVirtual,
    };
};
